import React, { useCallback, useMemo } from 'react'
import Text from 'core/elements/Text'
import { hotplugVirtualMachine } from './actions'
import useUpdateAction from 'core/hooks/useUpdateAction'
import ModalForm from 'core/elements/modal/ModalForm'
import useParams from 'core/hooks/useParams'
import TextField from 'core/components/validatedForm/TextField'
import Theme from 'core/themes/model'
import { makeStyles } from '@material-ui/styles'
import Info from 'core/components/validatedForm/Info'
import { getCurrentUser } from 'openstack/helpers'
import { hasOtherUserVm } from './helpers'
import { customValidator } from 'core/utils/fieldValidators'

export default function hotplugVmDialog({ rows: vms, onClose }) {
  const vm = vms?.[0]
  const classes = useStyles()
  const isHotPluggedVm = !!vm?.metadata?.['HOTPLUG_MEMORY']
  const isColdPluggedVm = vm?.state === 'stopped'
  const currentMemory = vm?.metadata?.['HOTPLUG_MEMORY']
  const memoryMax = vm?.metadata?.['HOTPLUG_MEMORY_MAX']
  const currentVcpus = vm?.metadata?.['HOTPLUG_CPU']
  const vcpusMax = vm?.metadata?.['HOTPLUG_CPU_MAX']

  const isHotColdPlugVm = isHotPluggedVm && isColdPluggedVm
  const memoryMin = isHotColdPlugVm ? 1 : currentMemory
  const vcpuMin = isHotColdPlugVm ? 1 : currentVcpus

  const memoryMessage = isHotColdPlugVm
    ? 'Memory must be between 1 and max values'
    : 'Memory must be between the current and max values'
  const vcpuMessage = isHotColdPlugVm
    ? 'vCPUs must be between 1 and max values'
    : 'vCPUs must be between the current and max values'

  const memoryValidator = useMemo(
    () =>
      customValidator((value) => {
        return memoryMax ? value >= memoryMin && value <= memoryMax : value >= memoryMin
      }, memoryMessage),
    [memoryMin, memoryMax, memoryMessage],
  )

  const vcpuValidator = useMemo(
    () =>
      customValidator((value) => {
        return vcpusMax ? value >= vcpuMin && value <= vcpusMax : value >= vcpuMin
      }, vcpuMessage),
    [vcpuMin, vcpusMax, vcpuMessage],
  )

  const defaultParams = {
    memory: null,
    vcpus: null,
  }
  const { params, getParamsUpdater, updateParams } = useParams(defaultParams)

  const { update, updating, error } = useUpdateAction(hotplugVirtualMachine)
  const submitForm = useCallback(async () => {
    const body = {
      hotplug: {
        cpu: params.vcpus,
        memory_mb: params.memory,
      },
    }
    const { success } = await update({
      id: vm.id,
      body,
    })
    if (success) onClose(true)
  }, [vm, params])

  const { role: userRole, userId } = getCurrentUser()

  return (
    <ModalForm
      open
      panel="dialog"
      title={`Hotplug VM`}
      onSubmit={submitForm}
      onClose={onClose}
      submitting={updating}
      error={error}
      submitTitle={`Hotplug VM`}
      maxWidth={600}
    >
      <div className={classes.body}>
        {userRole === 'member' && hasOtherUserVm({ vms, userId }) && (
          <Info error>NOTE: You are performing an operation on another user's VM.</Info>
        )}
        <Text variant="body2">
          Set new values for memory and CPU. These values must be set between the current and
          maximum values.
        </Text>
        <TextField
          id="memory"
          label={
            memoryMax
              ? `Hotplug RAM (MB) - Current ${currentMemory}, Max ${memoryMax}`
              : `Hotplug RAM (MB) - Current ${currentMemory}`
          }
          onChange={(val) => updateParams({ memory: val })}
          value={params.memory}
          info="The memory that will be hotplugged to the VM."
          type="number"
          validations={[memoryValidator]}
          required
        />
        <TextField
          id="vcpus"
          label={
            vcpusMax
              ? `Hotplug vCPUs - Current ${currentVcpus}, Max ${vcpusMax}`
              : `Hotplug vCPUs - Current ${currentVcpus}`
          }
          onChange={(val) => updateParams({ vcpus: val })}
          value={params.vcpus}
          info="The number of vcpus that will be hotplugged to the VM."
          type="number"
          validations={[vcpuValidator]}
          required
        />
      </div>
    </ModalForm>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  body: {
    display: 'grid',
    gap: 12,
  },
}))
